import { useEffect, useState, useCallback } from 'react'
import { AlertTriangle, Wallet, X } from 'lucide-react'
import { Button } from './ui/button'

/**
 * Яркая плашка в сайдбаре: предупреждает об окончании срока серверов /
 * нехватке баланса ДО того, как серверы встанут. Раньше клиент узнавал о
 * проблеме постфактум (сервер приостановлен) или только на странице баланса.
 *
 * Источник — тот же `/api/balance`, что и BalanceSection (проксируется на
 * лиц-сервер). Ничего не показывает, если тревоги нет, серверов нет или
 * балансовая система клиенту недоступна (404/ошибка).
 */

type BalanceData = {
  available_usdt?: number
  balance_usdt?: number
  auto_renew?: boolean
  server_count?: number
  est_months?: number
  monthly_total?: number
  price_per_server_month?: number
  underfunded_count?: number
  billing_breakdown?: { pay_date?: number }
}

type Level = 'critical' | 'warning' | null

const DISMISS_KEY = 'bb_balance_banner_dismiss_until'
const POLL_MS = 5 * 60 * 1000

function daysLeft(payDate: number): number {
  if (!payDate || payDate <= 0) return Infinity
  return Math.floor((payDate * 1000 - Date.now()) / 86400000)
}

export default function BalanceBanner({ onGoToBalance }: { onGoToBalance: () => void }) {
  const [data, setData] = useState<BalanceData | null>(null)
  const [dismissedUntil, setDismissedUntil] = useState<number>(() => {
    const v = Number(localStorage.getItem(DISMISS_KEY) || 0)
    return Number.isFinite(v) ? v : 0
  })

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/balance')
      if (!r.ok) { setData(null); return }
      const d = await r.json()
      if (d && typeof d === 'object') setData(d as BalanceData)
    } catch {
      /* нет балансовой системы / сеть — молча ничего не показываем */
    }
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, POLL_MS)
    return () => clearInterval(id)
  }, [load])

  if (!data) return null
  const servers = Number(data.server_count || 0)
  if (servers <= 0) return null

  const avail = Number(data.available_usdt ?? data.balance_usdt ?? 0)
  const monthly = Number(data.monthly_total || servers * Number(data.price_per_server_month || 170))
  const est = Number(data.est_months || 0)
  const underfunded = Number(data.underfunded_count || 0)
  const dLeft = daysLeft(Number(data.billing_breakdown?.pay_date || 0))
  const auto = !!data.auto_renew

  // Уровень тревоги (по убыванию срочности).
  let level: Level = null
  let title = ''
  let body = ''
  if (underfunded > 0) {
    level = 'critical'
    title = `Не хватило баланса на ${underfunded} серв.`
    body = `Серверы скоро приостановятся. Пополните баланс (${avail.toFixed(0)} USDT доступно).`
  } else if (dLeft <= 2) {
    level = 'critical'
    title = dLeft <= 1 ? 'Оплата серверов завтра' : `Оплата серверов через ${dLeft} дн.`
    body = auto
      ? `Спишется автоматически — убедитесь, что на балансе есть ~${monthly.toFixed(0)} USDT (сейчас ${avail.toFixed(0)}).`
      : `Пополните ~${monthly.toFixed(0)} USDT или включите авто-продление, иначе серверы приостановятся.`
  } else if (dLeft <= 5) {
    level = 'warning'
    title = `Скоро оплата серверов (через ${dLeft} дн.)`
    body = auto
      ? `Убедитесь, что на балансе хватит (~${monthly.toFixed(0)} USDT, сейчас ${avail.toFixed(0)}).`
      : `Пополните баланс или включите авто-продление.`
  } else if (est > 0 && est < 1) {
    level = 'warning'
    title = 'Баланса хватит меньше месяца'
    body = `На балансе ${avail.toFixed(0)} USDT для ${servers} серв. Рекомендуем пополнить.`
  }

  if (!level) return null
  // warning можно скрыть на 12ч; critical (нехватка/срок≤2дн) — не прячется.
  if (level === 'warning' && Date.now() < dismissedUntil) return null

  const isCritical = level === 'critical'
  const box = isCritical
    ? 'bg-destructive/10 border-destructive/30'
    : 'bg-amber-500/10 border-amber-500/30'
  const accent = isCritical ? 'text-destructive' : 'text-amber-600 dark:text-amber-500'

  const dismiss = () => {
    const until = Date.now() + 12 * 3600 * 1000
    localStorage.setItem(DISMISS_KEY, String(until))
    setDismissedUntil(until)
  }

  return (
    <div className={`mx-3 mb-3 p-3 rounded-lg border ${box} relative`}>
      {!isCritical && (
        <button
          onClick={dismiss}
          className="absolute top-1.5 right-1.5 text-muted-foreground hover:text-foreground"
          title="Скрыть на 12 часов"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      <div className="flex items-center gap-2 mb-1.5 pr-4">
        <AlertTriangle className={`w-4 h-4 shrink-0 ${accent}`} />
        <span className={`text-sm font-medium ${accent}`}>{title}</span>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">{body}</p>
      <Button size="sm" className="w-full h-7 text-xs gap-1.5" onClick={onGoToBalance}>
        <Wallet className="w-3 h-3" />Пополнить баланс
      </Button>
    </div>
  )
}
